import { Component, inject, signal, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SubscriptionService } from '../../services/subscription.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-payment-success',
  imports: [CommonModule],
  template: `
    <div class="page-container">
      <div class="success-card">
        @if (loading()) {
          <div class="spinner"></div>
          <h1>Zahlung wird verarbeitet...</h1>
          <p>Bitte warten Sie einen Moment.</p>
        } @else if (success()) {
          <div class="success-icon">✅</div>
          <h1>Zahlung erfolgreich!</h1>
          <p>Ihr Abonnement wurde aktiviert.</p>
          <button class="btn-primary" (click)="navigateToApp()">
            Zur Anwendung
          </button>
        } @else {
          <div class="error-icon">❌</div>
          <h1>Fehler bei der Verarbeitung</h1>
          <p>{{ errorMessage() }}</p>
          <button class="btn-secondary" (click)="navigateToHome()">
            Zurück zur Startseite
          </button>
        }
      </div>
    </div>
  `,
  styles: [`
    .page-container {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background-color: #f8fafc;
      padding: 20px;
    }

    .success-card {
      background-color: white;
      border-radius: 12px;
      padding: 48px;
      max-width: 500px;
      width: 100%;
      text-align: center;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .success-icon, .error-icon {
      font-size: 4rem;
      margin-bottom: 24px;
    }

    .spinner {
      width: 60px;
      height: 60px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #667eea;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 24px;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    h1 {
      font-size: 2rem;
      font-weight: 700;
      color: #1e293b;
      margin-bottom: 16px;
    }

    p {
      color: #64748b;
      font-size: 1.125rem;
      margin-bottom: 32px;
    }

    .btn-primary, .btn-secondary {
      padding: 12px 32px;
      font-size: 1.125rem;
      font-weight: 600;
      border-radius: 8px;
      border: none;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .btn-primary {
      background-color: #667eea;
      color: white;
    }

    .btn-primary:hover {
      background-color: #5a67d8;
      transform: translateY(-2px);
    }

    .btn-secondary {
      background-color: #f1f5f9;
      color: #475569;
    }

    .btn-secondary:hover {
      background-color: #e2e8f0;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentSuccessComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private subscriptionService = inject(SubscriptionService);

  loading = signal(true);
  success = signal(false);
  errorMessage = signal('');

  ngOnInit() {
    // Check if it's PayPal return (has token param) or Stripe (has session_id)
    this.route.queryParams.subscribe(params => {
      if (params['token']) {
        // PayPal order capture
        this.capturePayPalOrder(params['token']);
      } else if (params['session_id']) {
        // Stripe - payment already handled by webhook
        // Just mark as success
        localStorage.setItem('paymentCompleted', 'true');
        setTimeout(() => {
          this.loading.set(false);
          this.success.set(true);
        }, 1500);
      } else {
        this.loading.set(false);
        this.errorMessage.set('Ungültige Zahlungsparameter');
      }
    });
  }

  async capturePayPalOrder(orderId: string) {
    // Get token from sessionStorage (where AuthService stores it)
    const tokenDataStr = sessionStorage.getItem('authToken');
    if (!tokenDataStr) {
      this.loading.set(false);
      this.errorMessage.set('Nicht angemeldet');
      return;
    }

    let token: string;
    try {
      const tokenData = JSON.parse(tokenDataStr);
      token = tokenData.token;
    } catch {
      this.loading.set(false);
      this.errorMessage.set('Nicht angemeldet');
      return;
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    try {
      await this.http.post(
        `${environment.apiUrl}/payment/paypal/capture-order`,
        { orderId },
        { headers }
      ).toPromise();

      localStorage.setItem('paymentCompleted', 'true');
      this.loading.set(false);
      this.success.set(true);
    } catch (error) {
      this.loading.set(false);
      this.errorMessage.set('Fehler beim Erfassen der PayPal-Zahlung');
    }
  }

  navigateToApp() {
    // Setze Flag damit Landing Page das Modal schließt
    localStorage.setItem('paymentCompleted', 'true');
    localStorage.removeItem('selectedPlan');
    // Lade User Features nach erfolgreicher Zahlung
    this.subscriptionService.getUserFeatures().subscribe({
      next: () => {
        // Erst zur Landing Page (Modal schließen), dann automatisch zur App
        this.router.navigate(['/']).then(() => {
          setTimeout(() => {
            this.router.navigate(['/app']);
          }, 500);
        });
      },
      error: (err) => {
        console.error('Features laden fehlgeschlagen:', err);
        // Navigiere trotzdem zur App, auch wenn Features laden fehlschlägt
        this.router.navigate(['/']).then(() => {
          setTimeout(() => {
            this.router.navigate(['/app']);
          }, 500);
        });
      }
    });
  }

  navigateToHome() {
    localStorage.setItem('paymentCompleted', 'true');
    localStorage.removeItem('selectedPlan');
    this.router.navigate(['/']);
  }
}
